import type { AuthResponse } from "@/types/auth/auth";
import httpRequest from "@/utils/api";
import type { ICreateQRSessionResponse, IQRSessionResponse } from "@/api/auth/schema/auth";

export interface GeetestValidate {
  captcha_id: string;
  lot_number: string;
  captcha_output: string;
  pass_token: string;
  gen_time: string;
}

enum API {
  login = "/auth/login",
  loginWithSmsCode = "/auth/login-with-sms-code",
  register = "/auth/register",
  logout = "/auth/logout",
  sendCode = "/auth/send-code",
  sendSmsCode = "/auth/send-sms-code",
  resetPassword = "/auth/reset-password",
  geetestRegister = "/auth/geetest/register",
  wechatLogin = "/auth/wechat/login",
  createMiniappQrcodeSession = "/auth/miniapp/qrcode/session-token",
  checkQRSession = "/auth/qrcode/check-session",
}

const buildGeetestPayload = (geetestValidate: GeetestValidate) => ({
  captcha_id: geetestValidate.captcha_id,
  lot_number: geetestValidate.lot_number,
  captcha_output: geetestValidate.captcha_output,
  pass_token: geetestValidate.pass_token,
  gen_time: geetestValidate.gen_time,
});

export const loginAPI = (
  phone: string,
  password: string,
  geetestValidate: GeetestValidate
) => {
  return httpRequest.post<AuthResponse>({
    url: API.login,
    data: {
      phone: phone,
      password: password,
      geetest: buildGeetestPayload(geetestValidate),
    },
    encrypt: true,
  });
};

export const registerAPI = (
  username: string,
  phone: string,
  password: string,
  code: string
) => {
  return httpRequest.post<AuthResponse>({
    url: API.register,
    data: {
      username,
      phone,
      password,
      code,
    },
  });
};

export const logoutAPI = () => {
  return httpRequest.get({
    url: API.logout,
  });
};

export const sendCodeAPI = (email: string, geetestValidate: GeetestValidate) => {
  return httpRequest.post({
    url: API.sendCode,
    data: {
      email,
      geetest: buildGeetestPayload(geetestValidate),
    },
  });
};

export const sendSmsCodeAPI = (
  phone: string,
  geetestValidate: GeetestValidate,
  is_register: boolean = false
) => {
  return httpRequest.post({
    url: API.sendSmsCode,
    data: {
      phone,
      is_register,
      geetest: buildGeetestPayload(geetestValidate),
    },
  });
};

export const resetPasswordAPI = (
  phone: string,
  code: string,
  password: string
) => {
  return httpRequest.post({
    url: API.resetPassword,
    data: {
      phone,
      code,
      newPassword: password,
    },
  });
};

export const geetestRegisterAPI = () => {
  return httpRequest.get({
    url: API.geetestRegister,
  });
};

export const loginWithSmsCodeAPI = (phone: string, code: string) => {
  return httpRequest.post<AuthResponse>({
    url: API.loginWithSmsCode,
    data: {
      phone,
      code,
    },
    encrypt: true,
  });
};

interface WechatLoginResponse {
  access_token: string;
  expires_in: number;
  refresh_token: string;
  openid: string;
  scope: string;
  unionid?: string;
}

export const wechatLogin = (code: string) => {
  return httpRequest.post<WechatLoginResponse>({
    url: API.wechatLogin,
    data: { code },
  });
};

export const createMiniappQrcodeSession = () => {
  return httpRequest.post<ICreateQRSessionResponse>({
    url: '/auth/miniapp/qrcode/session-token',
  });
};

export const checkQRSessionAPI = (sessionToken: string) => {
  return httpRequest.get<IQRSessionResponse>({
    url: API.checkQRSession,
    params: {
      session_token: sessionToken,
    },
  });
};
